/**
 * The route's moments have to read in the order they happen.
 *
 *   pickup ≤ on-site (loading site) ≤ delivery ≤ cut-off
 *
 * Shared by the container order form and by the individual container's page,
 * because a container inherits these slots from its order and then edits them on
 * its own: one rule, one implementation, so a schedule the order refuses cannot
 * be reached by editing the container it produced. The server holds the same
 * rule in `validators/routeSequence.js` and is the actual gate.
 *
 * Enforced across *every* pair, not just neighbouring ones. Comparing only
 * neighbours is not enough, because a slot with a date but no time cannot rank
 * against a time and so breaks the chain: an on-site slot at 12:07 and a cut-off
 * at 12:05 on the same day both passed against a delivery that had a date only,
 * and the pair that actually conflicted was never compared.
 *
 * Every slot is optional, so the check runs over *what is present*: a slot with
 * no date drops out of the comparison instead of blocking the ones around it. A
 * missing time is deliberately not read as 00:00 — a container collected at
 * 15:00 and delivered the same day, with the delivery time not yet agreed, would
 * then compare 15:00 against midnight and be rejected for nothing. Two slots on
 * the same day are ranked by time only when both carry one.
 */

/**
 * The named moments of the three-stop route, and the message vocabulary.
 *
 * These four have a sentence each — "the cut-off is before the on-site slot" —
 * because they are the same four on every order and it is worth naming both ends
 * of a conflict in the reader's own words. A container's route can hold stops
 * these names do not cover, and those fall back to a generic sentence that
 * interpolates whatever the stops are actually called; see `messageKey`.
 */
export const ROUTE_SLOT_NAMES = [
  "pickup",
  "onSite",
  "delivery",
  "cutOff",
] as const;

export type NamedRouteSlot = (typeof ROUTE_SLOT_NAMES)[number];

/**
 * A slot's identity, for the caller to map a violation back to a field.
 *
 * Deliberately a plain string rather than the union above: a container's route
 * is variable, so its stops cannot be enumerated in advance and a caller needs
 * to be able to name them however it keys its own fields.
 */
export type RouteSlotName = string;

export interface RouteSlot {
  name: RouteSlotName;
  /**
   * What this slot is called in a sentence, for the stops the four named ones
   * do not cover. Ignored for those four, which have sentences of their own.
   */
  label?: string;
  /** `yyyy-mm-dd`, or null when this slot has no date. */
  day: string | null;
  /** `hh:mm`, or null when this slot has no time. */
  clock: string | null;
}

export interface RouteSlotViolation {
  slot: RouteSlot;
  conflictsWith: RouteSlot;
  /** Which half of the slot to report against. */
  part: "date" | "time";
  /** e.g. `validation:containerOrder.cutOffBeforeOnSite`. */
  messageKey: string;
  /**
   * Interpolation for the generic message, when the pair has no sentence of its
   * own. Absent for the four named slots.
   */
  values?: { later: string; earlier: string };
}

/** `yyyy-mm-dd`, from the form's string or a Date the API sent back. */
export const dayOf = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value.slice(0, 10);
  if (value instanceof Date && !Number.isNaN(value.getTime()))
    return value.toISOString().slice(0, 10);
  return null;
};

/** `hh:mm`, or null when the slot has no time. */
export const timeOf = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const isNamed = (slot: RouteSlot): slot is RouteSlot & { name: NamedRouteSlot } =>
  (ROUTE_SLOT_NAMES as readonly string[]).includes(slot.name);

/** What a slot is called in a sentence. */
const labelOf = (slot: RouteSlot) => slot.label?.trim() || slot.name;

/**
 * The sentence for one conflicting pair.
 *
 * The four named slots have a key each way round — six pairs, six keys — because
 * they are the same four on every order and a purpose-written sentence reads
 * better than an interpolated one. Any pair involving a stop outside that
 * vocabulary falls back to the generic key, which names both ends from their
 * labels: an added stop has no fixed name to build a key from, and inventing one
 * per stop would mean a translation file that could never be complete.
 */
const messageKey = (later: RouteSlot, earlier: RouteSlot) =>
  isNamed(later) && isNamed(earlier)
    ? `validation:containerOrder.${later.name}Before${
        earlier.name[0].toUpperCase() + earlier.name.slice(1)
      }`
    : "validation:containerOrder.slotOutOfOrder";

/** The interpolation the generic sentence needs, or nothing for the six keys. */
const messageValues = (later: RouteSlot, earlier: RouteSlot) =>
  isNamed(later) && isNamed(earlier)
    ? undefined
    : { later: labelOf(later), earlier: labelOf(earlier) };

/**
 * Every slot that lands before one before it.
 *
 * One pass, two running high-water marks — equivalent to comparing all pairs, in
 * O(n) time and O(1) extra space rather than O(n²). A slot can be beaten in
 * exactly two ways, so two marks suffice:
 *
 *  · `latestDay` — the latest date of any earlier slot. If it is past the slot
 *    being checked, that slot is out of order whatever the times are.
 *
 *  · `latestClock` — the latest (date, time) among earlier slots that *have* a
 *    time. It only bites on an equal date, and that is exact: if its date were
 *    later the first mark would already have caught it, and if its date were
 *    earlier then — being the maximum — every other timed slot is earlier still,
 *    so none of them can clash on this date either.
 *
 * All violations are returned, not just the first, so each offending field can
 * show its own message rather than surfacing one at a time as they are fixed.
 */
export function findRouteSequenceViolations(
  slots: RouteSlot[],
): RouteSlotViolation[] {
  const violations: RouteSlotViolation[] = [];

  let latestDay: { day: string; slot: RouteSlot } | null = null;
  let latestClock: { day: string; clock: string; slot: RouteSlot } | null = null;

  for (const slot of slots) {
    // A slot with no date takes no part — it neither fails nor constrains.
    if (!slot.day) continue;

    if (latestDay && latestDay.day > slot.day)
      violations.push({
        slot,
        conflictsWith: latestDay.slot,
        part: "date",
        messageKey: messageKey(slot, latestDay.slot),
        values: messageValues(slot, latestDay.slot),
      });
    else if (
      slot.clock &&
      latestClock &&
      latestClock.day === slot.day &&
      latestClock.clock > slot.clock
    )
      violations.push({
        slot,
        conflictsWith: latestClock.slot,
        part: "time",
        messageKey: messageKey(slot, latestClock.slot),
        values: messageValues(slot, latestClock.slot),
      });

    if (!latestDay || slot.day > latestDay.day)
      latestDay = { day: slot.day, slot };
    if (
      slot.clock &&
      (!latestClock ||
        slot.day > latestClock.day ||
        (slot.day === latestClock.day && slot.clock > latestClock.clock))
    )
      latestClock = { day: slot.day, clock: slot.clock, slot };
  }

  return violations;
}
